// The Agents page's llm_route PREVIEW read (#5207 wire, #5209 client) — "if a task
// arrived now, which model and key would it get, and if none, why", one entry per
// task lane plus `gardener` (`GET /v1/llm-routes/preview`, `packages/mcp/src/army/
// llm-route.ts` `LlmRoutePreviewView`).
//
// NO SHARED SCHEMA EXISTS for this wire: `@developerz/mcp` (the type's true home)
// is not a dashboard dependency, and no `@developerz/domain` resource publishes it
// either — the route projects it inline (`apps/api/src/deps/llm-routes.ts`
// `projectLlmRoutePreview`). This file is therefore a MIRROR, parsed through its
// own zod schema (never a bare cast) so a drift from the server fails here the same
// way `agent-activity.ts`'s domain-schema parse does. Reuses the domain's own
// `LlmRoutePurposeAnySchema` / `BYOKProviderSchema` rather than re-enumerating
// either vocabulary.
//
// POLLED at 60 s — slower than `useAgentActivity`'s 30 s because the server caches
// the computation for 15 s per account+repo (`LLM_ROUTE_PREVIEW_TTL_MS`) and this
// read fans out over every purpose. ONE query key: the rail's dot (`lib/
// rail-counts.ts`) and the page's cards (`AgentsView`/`AgentCard`) share this same
// hook, so mounting both never arms a second poll.

import {
  AGENT_ROSTER,
  type AgentId,
  BYOKProviderSchema,
  type LlmRoutePurposeAny,
  LlmRoutePurposeAnySchema,
} from '@developerz/domain';
import { createQuery } from '@tanstack/solid-query';
import { z } from 'zod';
import { activeAccountId } from './active-account';
import { apiFetch } from './api';
import { SESSION_TOKEN } from './session';

const PreviewPickWireSchema = z.object({
  provider: BYOKProviderSchema,
  model: z.string(),
  key_label: z.string().nullable(),
  source: z.enum(['pin', 'pool']),
});

/**
 * The route row is not what serves. `pin_unserved`: the agent's own selection names no
 * key that would serve it, so its work runs on `using` until the selection is fixed.
 */
const PreviewFallbackWireSchema = z.object({
  reason: z.enum(['cooling', 'credits_exhausted', 'model_parked', 'pin_unserved']),
  retry_at_iso: z.string().optional(),
  using: z.object({ provider: z.string(), model: z.string() }),
});

/**
 * The lane cannot START now, and `pick` is the key it WAITS for (never a key that would
 * run instead): every key it may run on is at capacity — under a selection, the selected
 * key alone — or its selected key is cooling. A timed wait, like `keys_cooling`.
 */
const PreviewWaitWireSchema = z.object({
  reason: z.enum(['saturated', 'cooling', 'credits_exhausted']),
  retry_at_iso: z.string().optional(),
  on: z.object({ provider: z.string(), key_label: z.string().nullable() }),
});

/** Every blocker kind the resolver's empty-pick grader answers (`PoolBlocker['kind']`), plus the gardener's own. */
export const PreviewBlockerKind = [
  'no_pool',
  'all_cooled',
  'lane_fenced',
  'models_parked',
  'keys_cooling',
  'no_pick_in_process',
] as const;
export type PreviewBlockerKind = (typeof PreviewBlockerKind)[number];

const PreviewBlockerWireSchema = z.object({
  kind: z.enum(PreviewBlockerKind),
  retry_at_iso: z.string().optional(),
});

const PreviewTemplatesWireSchema = z.object({
  presence: z.enum(['ok', 'unknown']),
  online_boxes: z.number().int().nonnegative(),
  models: z.array(z.string()),
});

export const AgentsPreviewEntryWireSchema = z.object({
  purpose: LlmRoutePurposeAnySchema,
  pick: PreviewPickWireSchema.nullable(),
  // Absent from an API pod older than the field (a rolling deploy): read as no wait.
  wait: PreviewWaitWireSchema.nullish(),
  fallback: PreviewFallbackWireSchema.nullable(),
  blocker: PreviewBlockerWireSchema.nullable(),
  stalls: z.boolean(),
  box_env: z.boolean(),
  templates: PreviewTemplatesWireSchema.optional(),
  overrides_templates: z.number().int().nonnegative().optional(),
});

export const AgentsPreviewWireSchema = z.object({
  repo_id: z.string().nullable(),
  as_of_iso: z.string(),
  purposes: z.array(AgentsPreviewEntryWireSchema),
});

export type AgentsPreviewEntryWire = z.infer<typeof AgentsPreviewEntryWireSchema>;
export type AgentsPreviewWire = z.infer<typeof AgentsPreviewWireSchema>;

export const AGENTS_PREVIEW_QUERY_KEY = 'agents_preview';

/** How often the preview is re-read. Slower than activity: the server itself only
 * recomputes every 15 s (`LLM_ROUTE_PREVIEW_TTL_MS`), so a faster poll here would
 * just re-ask for the same cached answer. */
export const AGENTS_PREVIEW_POLL_MS = 60_000;

export function fetchAgentsPreview(
  token: string | null,
  signal?: AbortSignal,
): Promise<AgentsPreviewWire> {
  return apiFetch('/v1/llm-routes/preview', token, signal ? { signal } : undefined, {
    schema: AgentsPreviewWireSchema,
  });
}

/**
 * The account's llm_route preview, org-scoped (no `repo_id`: the page has no repo
 * selector). Idle with no session; polled every {@link AGENTS_PREVIEW_POLL_MS}, and
 * keyed on the active org so a switch re-reads under the new tenant.
 */
export function useAgentsPreview(token: string | null = SESSION_TOKEN) {
  return createQuery<AgentsPreviewWire>(() => ({
    queryKey: [AGENTS_PREVIEW_QUERY_KEY, token, activeAccountId()],
    enabled: token !== null,
    refetchInterval: AGENTS_PREVIEW_POLL_MS,
    queryFn: ({ signal }) => fetchAgentsPreview(token, signal),
  }));
}

/**
 * The one purpose each preview-backed agent's card reads for its OWN "Right now"
 * line and status pill — the coder's DEFAULT lane, not every lane it owns.
 *
 * The Coder owns four purposes (`general`, `coding`, `quick`, `debugging-qa`,
 * `AGENT_ROSTER.purpose`); a stall confined to a task-tag lane lights the RAIL
 * DOT (via {@link agentsRailDotStatus}, which walks every purpose) without
 * turning the Coder's own card into a stall — the per-tag detail is the drawer's
 * "Fine-tune by task tag" rows (#5211), out of this issue's scope. DevOps has no
 * purpose of its own (`AGENT_ROSTER.modelFrom.devops`) and mirrors the Coder's.
 * Escalation and Default have no preview entry at all (#5207 Contract) and are
 * deliberately absent here.
 *
 * Triage names its OWN pin purpose (`triage`, #6388), so the Triage card reads
 * the purpose it owns; the preview read carries no `triage` entry today (the
 * server previews the TASK LANES plus the gardener, `PREVIEW_PURPOSES`, and
 * `triage` is a PIN-ONLY purpose), so the name is the card's own purpose, not a
 * promise of a preview row. The intake Screen has no preview line of its own
 * and is deliberately absent here.
 *
 * The values are typed as route PURPOSES, not free strings: a purpose the
 * vocabulary does not hold is a compile error here, never a row that silently
 * matches nothing in the read.
 */
export const AGENT_PREVIEW_PURPOSE: Readonly<Partial<Record<AgentId, LlmRoutePurposeAny>>> = {
  coder: 'general',
  devops: 'general',
  reviewer: 'review',
  ci_fixer: 'ci-triage',
  planner: 'plan',
  scout: 'scout',
  gardener: 'gardener',
  triage: 'triage',
};

/** The preview entry a card reads, or `undefined` when the agent has none (Escalation, Default) or the read has none for it yet. */
export function previewEntryFor(
  purposes: readonly AgentsPreviewEntryWire[] | undefined,
  agent: AgentId,
): AgentsPreviewEntryWire | undefined {
  const purpose = AGENT_PREVIEW_PURPOSE[agent];
  if (purpose === undefined || purposes === undefined) return undefined;
  return purposes.find((p) => p.purpose === purpose);
}

/** The activity shape the dot fold needs — just the mode flag, per agent.
 * `mode_enabled?: boolean | undefined`, not the narrower `mode_enabled?: boolean`:
 * the domain's own `AgentActivityEntryWire` (a zod `.optional()`) types the field
 * that way, and `exactOptionalPropertyTypes` treats the two as different shapes. */
export type ActivityModeFlags = readonly {
  readonly id: AgentId;
  readonly mode_enabled?: boolean | undefined;
}[];

/**
 * Is this lane stalled in the way that is a problem to fix — the ONE stall
 * predicate (#5624): `stalls` with a blocker that is not `keys_cooling` (a
 * cooling pool is a timed wait with a retry time, never a stall) and no `wait` (the
 * lane's selected key is full or cooling: it starts after a stream frees or the
 * cooldown lapses — Waiting on the card, never Stuck). The rail dot
 * ({@link agentsRailDotStatus}) and a card's Stuck pill (`agentStatus`) both read
 * it, so neither can drift from the other. It says nothing of whether the owning
 * agent is switched on — each caller decides that against its own activity read.
 */
export function previewStalls(entry: AgentsPreviewEntryWire): boolean {
  return entry.stalls && entry.blocker?.kind !== 'keys_cooling' && entry.wait == null;
}

/**
 * The rail dot (#4395 twin of Terminal's, epic #5200): `down` when an ENABLED
 * agent's work is actually stalled, else `undefined` (no dot — the same honesty
 * rule `count` follows: unknown is never painted as healthy).
 *
 * Walks EVERY purpose in the read (not just the six cards' own primary lane):
 * `AGENT_ROSTER.purpose[entry.purpose]` names the owning agent, so a stall on
 * `coding`/`quick`/`debugging-qa` lights the Coder exactly as `general` would.
 * `keys_cooling` never lights it — a cooling pool is a timed wait with a retry
 * time, warning tone on the card, never the rail — which is also why a
 * `keys_cooling` blocker on the REVIEWER draws no dot even though `stalls` is
 * true for every blocker on review (`resolve-preview.ts`): the exclusion is on
 * the BLOCKER KIND, checked before the agent's identity.
 *
 * A `wait` never lights it either, for the same reason: the lane starts when a stream
 * frees or the selected key's cooldown lapses — warning tone on the card, never the rail.
 *
 * `undefined` for either input (not loaded, or refused) answers no dot at all —
 * never a guessed tone from a partial read.
 *
 * Returns the literal `'down'`, never `AppNav`'s `NavDotStatus` union: this
 * file is reachable from `lib/mocks.ts` (`agents-preview-mocks.ts`), which
 * `scripts/`' own non-composite tsc project reads by relative import with no
 * `jsx` compiler option — importing a TYPE from a `.tsx` component here would
 * pull that component's whole module into a program that cannot parse it
 * (TS6142, the exact defect `apps/dashboard/e2e/tsconfig.json`'s docblock
 * names). `'down'` is a structural subtype of `NavDotStatus`, so `rail-counts.ts`
 * assigns this straight into its own `NavDotStatus`-typed `RailDots` map with
 * no cast.
 */
export function agentsRailDotStatus(
  purposes: readonly AgentsPreviewEntryWire[] | undefined,
  activity: ActivityModeFlags | undefined,
): 'down' | undefined {
  if (purposes === undefined || activity === undefined) return undefined;
  const enabledOf = new Map(activity.map((a) => [a.id, a.mode_enabled]));
  for (const entry of purposes) {
    if (!previewStalls(entry)) continue;
    const owner = AGENT_ROSTER.purpose[entry.purpose];
    // No mode governs this agent (Planner, Gardener, …) → counts as enabled.
    const enabled = enabledOf.get(owner) ?? true;
    if (enabled) return 'down';
  }
  return undefined;
}
